'use client';

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, ChevronsRight, Clock3, ExternalLink } from 'lucide-react';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { StoriesWithDetailEyecatches } from '@/types/stories';
import { formatDateStringWithDelimiter } from '@/utils/formatters/dateFormatter';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import TruncateText from '@/components/TruncateText';
import 'keen-slider/keen-slider.min.css';

export interface StoriesListUnitProps {
    data: StoriesWithDetailEyecatches;
    showMoreButton: boolean;
}

export default function StoriesListUnit({ data, showMoreButton }: StoriesListUnitProps) {
    const { safePush } = useSafeNavigation();
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const handleClick = () => {
        safePush(`/stories/detail/${data.cd}`);
    };

    const details = data.stories_details ?? [];

    const tiles = useMemo(() => {
        const eyecatchNum = 9;

        const imageTiles = details.map((d) => {
            if (d.eyecatch_public_url) {
                return {
                    type: 'image' as const,
                    key: `detail-${d.stories_hub_news_id}`,
                    url: d.eyecatch_public_url,
                };
            }
            return {
                type: 'image' as const,
                key: `default-${d.stories_hub_news_id}`,
                url: `/images/hub/map/icon-${d.hub_type}.svg`,
            };
        });

        let limitedImageTiles = imageTiles;

        if (isMounted && imageTiles.length > eyecatchNum) {
            const shuffled = [...imageTiles];
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            limitedImageTiles = shuffled.slice(0, eyecatchNum);
        } else {
            limitedImageTiles = imageTiles.slice(0, eyecatchNum);
        }

        const fallbackCount = eyecatchNum - limitedImageTiles.length;

        const layout: ('image' | 'fallback')[] = [
            ...Array(limitedImageTiles.length).fill('image'),
            ...Array(fallbackCount).fill('fallback'),
        ];

        if (isMounted) {
            for (let i = layout.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [layout[i], layout[j]] = [layout[j], layout[i]];
            }
        }

        const fallbackTailwindColors = [
            'bg-color-schoomy1',
            'bg-color-schoomy2',
            'bg-color-schoomy3',
            'bg-color-schoomy4',
        ];

        let imageIndex = 0;
        let fallbackColorIndex = 0;

        return layout.map((slot, index) => {
            if (slot === 'image') {
                const tile = limitedImageTiles[imageIndex];
                imageIndex++;
                return tile;
            }
            const colorClass = fallbackTailwindColors[fallbackColorIndex % fallbackTailwindColors.length];
            fallbackColorIndex++;

            return {
                type: 'fallback' as const,
                key: `fallback-${index}`,
                colorClass,
            };
        });
    }, [data.cd, details, isMounted]);

    return (
        <>
            <div
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full bg-color-white-base text-color-base-layout-logo rounded-none md:rounded-lg cursor-pointer"
                onClick={handleClick}
            >
                <div className="flex flex-col justify-between h-full">
                    <div className="flex flex-col">
                        <div className="font-corp font-bold text-lg md:text-md lg:text-xl xl:text-3xl  w-full mb-1 lg:mb-3">
                            {data.title}
                        </div>
                        <div className="flex items-center font-corp font-semibold text-color-base-2">
                            <IconAdaptive
                                Icon={BookOpen}
                                strokeWidth={2.0}
                                baseSize={20}
                                breakpoints={{ md: 20 }}
                                className="mr-[4px] mt-[2px]"
                            />
                            <span className="text-base xl:text-xl">{details.length} 本のストーリー</span>
                        </div>
                    </div>
                    <div className="flex grid grid-cols-3 justify-start items-start text-color-base-layout-logo mt-3">
                        <div className="col-span-3 md:col-span-2 lg:col-span-3 xl:col-span-2 md:pr-4">
                            <div className="relative overflow-hidden rounded-none md:rounded-lg aspect-[4/3] w-full">
                                <motion.img
                                    src={data.eyecatch_public_url}
                                    alt="stories-image"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                        </div>
                        <div className="col-span-3 md:col-span-1 lg:col-span-3 xl:col-span-1 flex flex-col justify-between h-full">
                            <div className="font-notojp text-sm whitespace-pre-line mb-2">
                                <TruncateText
                                    text={data.description}
                                    maxLength={200}
                                    breakpoints={{ md: 200, lg: 80, xl: 100 }}
                                />
                            </div>
                            <div className="flex justify-end items-end w-full font-cantarell font-bold text-[10px] text-color-base pt-2">
                                <IconAdaptive
                                    Icon={Clock3}
                                    strokeWidth={2.0}
                                    baseSize={14}
                                    breakpoints={{ md: 14 }}
                                    className="mr-[2px]"
                                />
                                <TruncateText
                                    text={formatDateStringWithDelimiter(data.published_date)}
                                    maxLength={40}
                                    breakpoints={{ md: 30 }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-3">
                    {tiles.map((tile, index) => {
                        let cornerClass = '';
                        if (index === 6) cornerClass += ' md:rounded-bl-lg lg:rounded-bl-none';
                        if (index === 8) cornerClass += ' md:rounded-br-lg';
                        if (index === 2) cornerClass += ' lg:rounded-tr-lg';
                        if (tile.type === 'image') {
                            return (
                                <div
                                    key={tile.key}
                                    className={`w-full ${cornerClass}`}
                                    style={{
                                        paddingBottom: '66.666%',
                                        backgroundImage: `url(${tile.url})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                    }}
                                />
                            );
                        }
                        return (
                            <div
                                key={tile.key}
                                className={`relative w-full flex items-center justify-center ${tile.colorClass} ${cornerClass}`}
                                style={{
                                    paddingBottom: '66.666%',
                                }}
                            >
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <ImageAdaptive
                                        src="/images/stories/schoomy.png"
                                        baseWidth={80}
                                        breakpoints={{ sm: 100, md: 120, lg: 120, xl: 140 }}
                                        className="opacity-90"
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {showMoreButton && (
                <div
                    className={`
                        flex items-center font-notojp font-bold text-color-base text-sm cursor-pointer mt-2
                        ${data.url ? 'justify-between' : 'justify-end'}
                    `}
                    onClick={handleClick}
                >
                    {data.url && (
                        <a
                            href={data.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-2 text-color-base-layout-logo transition-colors"
                        >
                            <IconAdaptive
                                Icon={ExternalLink}
                                strokeWidth={2.0}
                                baseSize={18}
                                breakpoints={{ md: 20 }}
                            />
                            <span className="font-notojp font-semibold text-sm underline">
                                {data.link_label || 'リンクを見る'}
                            </span>
                        </a>
                    )}
                    <div className="flex items-center">
                        <IconAdaptive
                            Icon={ChevronsRight}
                            baseSize={22}
                            breakpoints={{ md: 24 }}
                            className="mt-[2px]"
                        />
                        もっとみる
                    </div>
                </div>
            )}
        </>
    );
}
